import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import { ExpandMoreRounded } from '@mui/icons-material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

const GPIControls = ({ captionId, version, scenePath, gpiDataFromQuery }) => {
  const [isGpiOpen, setIsGpiOpen] = useState(true);
  const [gpiDefinitions, setGpiDefinitions] = useState([]);
  const [mergedGpiData, setMergedGpiData] = useState([]);
  const [hasGpiData, setHasGpiData] = useState(false);
  const [gpiFileExists, setGpiFileExists] = useState(false);
  const [gpiFileUrl, setGpiFileUrl] = useState('');
  const [fileGpiStates, setFileGpiStates] = useState({});

  useEffect(() => {
    setGpiDefinitions([]);
    setMergedGpiData([]);
    setHasGpiData(false);
    setGpiFileExists(false);
    setFileGpiStates({});

    if (captionId && version && scenePath) {
      const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
      const url = `${baseAeroUrl}${scenePath}/${captionId}/${version}/GPI.json`;
      setGpiFileUrl(url);
      checkGpiFileExists(captionId, version, scenePath);
    }
  }, [captionId, version, scenePath]);

  useEffect(() => {
    if (gpiFileExists && captionId && version && scenePath) {
      loadGpiDefinitions(captionId, version, scenePath);
    }
  }, [gpiFileExists, captionId, version, scenePath]);

  useEffect(() => {
    if (gpiDefinitions.length > 0 || (gpiDataFromQuery && Array.isArray(gpiDataFromQuery) && gpiDataFromQuery.length > 0)) {
      const merged = mergeGpiData(gpiDefinitions, gpiDataFromQuery);
      setMergedGpiData(merged);
      setHasGpiData(merged.length > 0);
      console.log(`Caption ${captionId} has GPI data:`, merged);
    } else {
      setHasGpiData(false);
      console.log(`Caption ${captionId} has NO GPI data`);
    }
  }, [gpiDefinitions, gpiDataFromQuery, captionId]);

  async function checkGpiFileExists(captionId, version, scenePath) {
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const gpiUrl = `${baseAeroUrl}${scenePath}/${captionId}/${version}/GPI.json`;

    try {
      const response = await fetch(gpiUrl, { method: 'HEAD' });
      const exists = response.ok;
      console.log(`GPI file check for ${captionId}: ${exists ? 'EXISTS' : 'NOT FOUND'}`);
      setGpiFileExists(exists);

      if (exists) {
        fetchFileGpiStates(gpiUrl);
      }

      return exists;
    } catch (error) {
      console.error(`Error checking if GPI file exists for ${captionId}:`, error);
      setGpiFileExists(false);
      return false;
    }
  }

  async function fetchFileGpiStates(url) {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          const stateMap = {};
          data.forEach(item => {
            stateMap[item.id] = item.state;
          });
          setFileGpiStates(stateMap);
          console.log("File GPI states:", stateMap);
        }
      }
    } catch (error) {
      console.error("Error fetching GPI file states:", error);
    }
  }

  async function loadGpiDefinitions(captionId, version, scenePath) {
    const baseAeroUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
    const gpiUrl = `${baseAeroUrl}${scenePath}/${captionId}/${version}/GPI.json`;

    try {
      const response = await fetch(gpiUrl);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          console.log(`Loaded ${data.length} GPI definitions for ${captionId}`);
          setGpiDefinitions(data);
          return true;
        } else {
          console.log(`GPI file exists for ${captionId} but contains no data`);
          setGpiDefinitions([]);
          return false;
        }
      } else {
        console.log(`Failed to load GPI data for ${captionId}, status: ${response.status}`);
        setGpiDefinitions([]);
        return false;
      }
    } catch (error) {
      console.error(`Error loading GPI definitions for ${captionId}:`, error);
      setGpiDefinitions([]);
      return false;
    }
  }

  function mergeGpiData(definitions, queryData) {
    if ((!definitions || definitions.length === 0) &&
      (!queryData || !Array.isArray(queryData) || queryData.length === 0)) {
      return [];
    }

    if (definitions.length > 0 && (!queryData || !Array.isArray(queryData) || queryData.length === 0)) {
      return definitions;
    }

    if ((!definitions || definitions.length === 0) && queryData && Array.isArray(queryData) && queryData.length > 0) {
      return queryData;
    }

    const actionMap = {};
    definitions.forEach(def => {
      actionMap[def.id] = def.action;
    });

    const stateMap = {};
    queryData.forEach(item => {
      stateMap[item.id] = item.state;
    });

    const result = definitions.map(def => ({
      ...def,
      state: stateMap[def.id] !== undefined ? stateMap[def.id] : def.state
    }));

    queryData.forEach(item => {
      if (!actionMap[item.id]) {
        result.push({
          id: item.id,
          state: item.state,
          action: item.action || "Unknown"
        });
      }
    });

    return result;
  }

  const downloadGpiFile = async () => {
    if (!gpiFileUrl) return;
    try {
      const response = await fetch(gpiFileUrl);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `GPI.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error(`Failed to download GPI file: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading GPI file:', error);
    }
  };

  // OPUS-1148: render only when there is REAL GPI data (a known action) — hide
  // the whole section otherwise (no empty "No GPI data available" panel, no
  // flicker from transient/empty subscription pushes). Matches the other
  // Scene-Controls accordions' design (transparent surface + bottom border).
  const displayableGpi = mergedGpiData.filter((gpi) => gpi.action && gpi.action !== "Unknown");
  if (displayableGpi.length === 0) {
    return null;
  }

  return (
    <Accordion
      expanded={isGpiOpen}
      onChange={() => setIsGpiOpen(!isGpiOpen)}
      sx={{ backgroundColor: 'transparent !important', borderRadius: 0, overflow: 'hidden', flexShrink: 0, '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid var(--border)', margin: 0, '&.Mui-expanded': { margin: '0 0 4px 0', backgroundColor: 'transparent !important' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded sx={{ color: 'var(--text-dim)', fontSize: 20 }} />}
        sx={{ height: 'auto', minHeight: '2.6em !important', px: 1, py: 0.75, backgroundColor: 'transparent !important', '&.Mui-expanded': { minHeight: '2.6em !important' }, '& .MuiAccordionSummary-content': { margin: 0, justifyContent: 'flex-end', alignItems: 'center' } }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>GPI CONTROLS</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, backgroundColor: 'transparent !important' }}>
        <List sx={{ width: '100%', p: 0 }}>
          {displayableGpi.map((gpi, index) => (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom: index !== displayableGpi.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                    py: 1,
                    px: 0.8,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={`ID: ${gpi.id}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: 'white',
                              mr: 1,
                              height: '24px',
                              '& .MuiChip-label': {
                                padding: '0 8px'
                              }
                            }}
                          />
                          {gpi.state ? (
                            <ToggleOnIcon color="seafoamGreen" sx={{ fontSize: 24 }} />
                          ) : (
                            <ToggleOffIcon color="disabled" sx={{ fontSize: 24 }} />
                          )}
                          <Typography sx={{ ml: 1, color: gpi.state ? '#45FFA7' : '#FFFFFF80' }}>
                            {gpi.state ? "TRUE" : "FALSE"}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={fileGpiStates[gpi.id] !== undefined ?
                              (fileGpiStates[gpi.id] ? "File: TRUE" : "File: FALSE") :
                              "File: N/A"}
                            size="small"
                            color={fileGpiStates[gpi.id] === true ? "success" :
                              fileGpiStates[gpi.id] === false ? "default" : "warning"}
                            variant="outlined"
                            sx={{
                              height: '24px',
                              minWidth: '85px',
                              '& .MuiChip-label': {
                                padding: '0 8px',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase'
                              }
                            }}
                          />

                          <Box
                            sx={{
                              display: 'flex',
                              border: '1px solid rgba(255, 255, 255, 0.12)',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}
                          >
                            <ToggleButton
                              value={gpi.action}
                              sx={{
                                p: 0,
                                px: 1.5,
                                backgroundColor: 'transparent',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                height: '24px',
                                textTransform: 'uppercase',
                                fontSize: '0.75rem',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                }
                              }}
                              onClick={downloadGpiFile}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {gpi.action}
                              </Box>
                            </ToggleButton>
                          </Box>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default GPIControls;